import { useSyncExternalStore } from "react";

import type { AltitudeUnit } from "@tripcart/shared";

const UNIT_KEY = "altUnit";
const NARROW_PX = 520;

/** `localStorage` can be blocked in private browsing or an iframe; the switch
 *  then simply does not persist (spec §7.5). */
function readStoredUnit(): AltitudeUnit | null {
  try {
    const v = localStorage.getItem(UNIT_KEY);
    return v === "m" || v === "ft" ? v : null;
  } catch {
    return null;
  }
}

export function storeUnit(unit: AltitudeUnit): void {
  try {
    localStorage.setItem(UNIT_KEY, unit);
  } catch {
    // Nothing to do: the choice still applies for this page view.
  }
}

const unitListeners = new Set<() => void>();

export function notifyUnitChanged(): void {
  unitListeners.forEach((l) => l());
}

function subscribeUnit(listener: () => void): () => void {
  unitListeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    unitListeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

/** The traveler's remembered unit, `null` on the server and when unset. */
export function useStoredUnit(enabled: boolean): AltitudeUnit | null {
  return useSyncExternalStore(
    subscribeUnit,
    () => (enabled ? readStoredUnit() : null),
    () => null,
  );
}

/** Whether the chart host is narrow enough to thin the day labels. Re-reads
 *  only when the width moves by 40px or more (spec §5.5). */
export function useIsNarrow(host: React.RefObject<HTMLDivElement | null>) {
  return useSyncExternalStore(
    (onChange) => {
      const el = host.current;
      if (!el || typeof ResizeObserver === "undefined") return () => {};
      let lastW = el.clientWidth;
      const ro = new ResizeObserver(() => {
        const w = el.clientWidth;
        if (Math.abs(w - lastW) < 40) return;
        lastW = w;
        onChange();
      });
      ro.observe(el);
      return () => ro.disconnect();
    },
    () => (host.current?.clientWidth ?? NARROW_PX) < NARROW_PX,
    () => false,
  );
}
